import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'tacoma-theme';
const VALID = ['auto', 'light', 'dark'];

function readStored() {
    try {
        const v = localStorage.getItem(STORAGE_KEY);
        if (VALID.includes(v)) return v;
    } catch (_) {  }
    return 'light';
}

function applyToHtml(t, systemPrefersDark = false) {
    if (typeof document === 'undefined') return;
    document.documentElement.setAttribute('data-theme', t);
    const effective = t === 'auto' ? (systemPrefersDark ? 'dark' : 'light') : t;
    document.documentElement.setAttribute('data-bs-theme', effective);
}

export function useTheme() {
    const [theme, setThemeState] = useState(readStored);
    const [systemPrefersDark, setSystemPrefersDark] = useState(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return false;
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
    });

    useEffect(() => {
        applyToHtml(theme, systemPrefersDark);
        try { localStorage.setItem(STORAGE_KEY, theme); } catch (_) {}
    }, [theme, systemPrefersDark]);

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const onChange = (e) => setSystemPrefersDark(e.matches);
        if (mq.addEventListener) mq.addEventListener('change', onChange);
        else mq.addListener(onChange);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', onChange);
            else mq.removeListener(onChange);
        };
    }, []);

    const setTheme = useCallback((t) => {
        if (!VALID.includes(t)) return;
        setThemeState(t);
    }, []);

    const cycleTheme = useCallback(() => {
        setThemeState((cur) => {
            const idx = VALID.indexOf(cur);
            return VALID[(idx + 1) % VALID.length];
        });
    }, []);

    const effective = theme === 'auto' ? (systemPrefersDark ? 'dark' : 'light') : theme;

    return {
        theme,
        effective,
        isDark: effective === 'dark',
        setTheme,
        cycleTheme,
    };
}

export default useTheme;
